/**
 * 정산 내역 정보 상세.
 * admin-ruda-web `prescriptionSettlementsDetails.js` 포팅.
 */
import {
  useDeleteUserSettlement,
  useUserSettlementDetails,
} from '@/entities/settlement/hooks/useSettlements';
import { EmptyState } from '@/shared/components/EmptyState';
import { DataCard } from '@/shared/components/DataCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import {
  formatBasicFeeType,
  formatDetailFeeApplied,
  getApprovalStatusBadgeVariant,
  getApprovalStatusLabel,
  getBasicFeeTypeBadgeVariant,
  getDetailFeeAppliedBadgeVariant,
} from '@/pages/prescriptions/prescriptionUtils';
import {
  formatCommissionRatePercent,
  formatPlatformFeeRatePercent,
  formatSettlementAmount,
  formatSettlementYearMonth,
  getSettlementApprovalStatusMeta,
} from '@/pages/settlements/settlementUtils';
import { formatPhoneNumber } from '@/pages/users/userListUtils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { Banknote, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

export const PrescriptionSettlementDetailPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const router = useRouter();
  const { userEmail } = useParams({ strict: false }) as { userEmail: string };
  const { prescriptionYm, settlementYm, userId } = useSearch({
    strict: false,
  }) as {
    prescriptionYm?: string;
    settlementYm?: string;
    userId?: number;
  };

  const email = decodeURIComponent(userEmail ?? '');
  const prescriptionYearMonth = prescriptionYm ?? '';
  const settlementYearMonth = settlementYm ?? '';
  const canDeleteSettlement =
    !!prescriptionYearMonth &&
    !!settlementYearMonth &&
    userId != null &&
    Number.isFinite(userId);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: rows = [], isLoading, isError, error } =
    useUserSettlementDetails(
      email || null,
      prescriptionYearMonth || null,
      settlementYearMonth || null,
    );

  const deleteSettlementMutation = useDeleteUserSettlement();

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const handleExit = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.history.back();
      return;
    }
    navigate({ to: ROUTE_PATHS.SETTLEMENT_PRESCRIPTIONS } as never);
  };

  const handleDeleteSettlement = async () => {
    if (!canDeleteSettlement) {
      toast.error('삭제에 필요한 사용자 정보가 없습니다. 목록에서 다시 진입해 주세요.');
      return;
    }

    try {
      await deleteSettlementMutation.mutateAsync({
        prescriptionYearMonth,
        settlementYearMonth,
        userId,
      });
      toast.success('정산 내역을 삭제했습니다.');
      setDeleteDialogOpen(false);
      navigate({ to: ROUTE_PATHS.SETTLEMENT_PRESCRIPTIONS } as never);
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const summaryEmail = rows[0]?.email || email || '-';
  const summaryCeoName = rows[0]?.ceoName || '-';
  const summaryPhone = formatPhoneNumber(rows[0]?.phoneNumber);
  const totalSettlementAmount = useMemo(
    () => rows.reduce((sum, row) => sum + (row.finalAmount ?? 0), 0),
    [rows],
  );

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Banknote}
        title="정산 내역 정보 상세"
        description="사용자별 정산 내역 상세 조회"
        actions={
          <>
            <MenuWriteGate>
              <Button
                variant="destructive"
                size="sm"
                disabled={!canDeleteSettlement || deleteSettlementMutation.isPending}
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                정산 내역 삭제
              </Button>
            </MenuWriteGate>
            <Button variant="outline" size="sm" onClick={handleExit}>
              나가기
            </Button>
          </>
        }
      />

      {canDeleteSettlement ? null : (
        <p className="text-sm text-muted-foreground">
          정산 삭제는 목록에서 해당 행을 선택해 진입해야 합니다.
        </p>
      )}

      {isLoading ? (
        <LoadingSpinner />
      ) : rows.length === 0 ? (
        <EmptyState description="정산 상세 데이터가 없습니다." />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>기본 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <Field label="사용자 이메일">
                  <Input disabled value={summaryEmail} />
                </Field>
                <Field label="대표자명">
                  <Input disabled value={summaryCeoName} />
                </Field>
                <Field label="연락처">
                  <Input disabled value={summaryPhone} />
                </Field>
                <Field label="처방 년월">
                  <Input
                    disabled
                    value={formatSettlementYearMonth(prescriptionYearMonth)}
                  />
                </Field>
                <Field label="정산 년월">
                  <Input
                    disabled
                    value={formatSettlementYearMonth(settlementYearMonth)}
                  />
                </Field>
                <Field label="총 정산 금액">
                  <Input
                    disabled
                    value={formatSettlementAmount(totalSettlementAmount)}
                    className="text-right"
                  />
                </Field>
              </div>
            </CardContent>
          </Card>

          <DataCard>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>사용자 이메일</TableHead>
                    <TableHead>대표자명</TableHead>
                    <TableHead>처방 년 월</TableHead>
                    <TableHead>제약사 명</TableHead>
                    <TableHead>상위업체</TableHead>
                    <TableHead>정산업체</TableHead>
                    <TableHead>병의원 명</TableHead>
                    <TableHead>의약품 명</TableHead>
                    <TableHead>의약품 코드</TableHead>
                    <TableHead className="text-right">의약품 단가</TableHead>
                    <TableHead className="text-right">총 처방 수량</TableHead>
                    <TableHead className="text-right">총 처방 금액</TableHead>
                    <TableHead>처방 승인 결과</TableHead>
                    <TableHead>정산 승인 결과</TableHead>
                    <TableHead>비고</TableHead>
                    <TableHead>기본 수수료율</TableHead>
                    <TableHead>상세 수수료율</TableHead>
                    <TableHead>표준수당율</TableHead>
                    <TableHead>플랫폼 수수료율</TableHead>
                    <TableHead>정산 수수료율</TableHead>
                    <TableHead className="text-right">정산 금액</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const settlementApproval = getSettlementApprovalStatusMeta(
                      row.pharmaCompanySettlementStatus,
                    );

                    return (
                      <TableRow
                        key={row.settlementId}
                        className={
                          Number(row.finalAmount) < 0
                            ? 'text-ruda-error'
                            : undefined
                        }
                      >
                        <TableCell>{row.email || '-'}</TableCell>
                        <TableCell>{row.ceoName || '-'}</TableCell>
                        <TableCell>
                          {formatSettlementYearMonth(row.prescriptionYearMonth)}
                        </TableCell>
                        <TableCell>{row.pharmaCompanyName || '-'}</TableCell>
                        <TableCell>{row.parentCompanyName || '-'}</TableCell>
                        <TableCell>{row.settlementCompanyName || '-'}</TableCell>
                        <TableCell>{row.clinicName || '-'}</TableCell>
                        <TableCell>{row.drugName || '-'}</TableCell>
                        <TableCell>{row.drugCode || '-'}</TableCell>
                        <TableCell className="text-right">
                          {formatSettlementAmount(row.price)}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.totalQuantity?.toLocaleString('ko-KR') ?? '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatSettlementAmount(row.totalAmount)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={getApprovalStatusBadgeVariant(
                              row.prescriptionApprovalStatus,
                            )}
                          >
                            {getApprovalStatusLabel(
                              row.prescriptionApprovalStatus,
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={settlementApproval.variant}>
                            {settlementApproval.label}
                          </Badge>
                        </TableCell>
                        <TableCell>{row.remarks || '-'}</TableCell>
                        <TableCell>
                          <Badge
                            variant={getBasicFeeTypeBadgeVariant(
                              row.basicFeeType,
                            )}
                          >
                            {formatBasicFeeType(row.basicFeeType)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={getDetailFeeAppliedBadgeVariant(
                              row.isDetailFeeApplied,
                            )}
                          >
                            {formatDetailFeeApplied(row.isDetailFeeApplied)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {formatCommissionRatePercent(
                            row.standardCommissionRate,
                          )}
                        </TableCell>
                        <TableCell>
                          {formatPlatformFeeRatePercent(row.platformFeeRate)}
                        </TableCell>
                        <TableCell>
                          {formatCommissionRatePercent(row.commissionRate)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatSettlementAmount(row.finalAmount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            </div>
          </DataCard>
        </>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>정산 내역 삭제</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  {formatSettlementYearMonth(prescriptionYearMonth)} 처방 ·{' '}
                  {formatSettlementYearMonth(settlementYearMonth)} 정산 ·{' '}
                  {summaryEmail} 사용자의 정산 내역을 삭제합니다.
                </p>
                <p>
                  해당 키에 속한 settlement 행이 모두 삭제되며, 제약사 원천
                  데이터(settlement_pharma)는 유지됩니다.
                </p>
                <p>
                  삭제 후 복구하려면 정산 정보 생성과 제약사 정산정보 업데이트를
                  다시 실행해야 합니다. 공개된 정산도 삭제됩니다.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteSettlementMutation.isPending}
              onClick={() => void handleDeleteSettlement()}
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
